import { clsx } from "clsx";
import type { CSSProperties, ReactNode } from "react";

export type EyebrowSize = "2xs" | "xs" | "sm" | "base";

export type EyebrowProps = {
  /** "inherit" applies no color class — use on non-default backgrounds (e.g. a colored card) where the eyebrow should pick up the surrounding text color. */
  tone?: "primary" | "muted" | "inherit";
  /**
   * Font size step — defaults to "xs" (12px, the standard eyebrow size).
   * "base" (16px) for banner-scale eyebrows (QuizStepReview, HowItWorks);
   * "sm"/"2xs" for the documented modal-header and dense-metadata cases.
   * Use this rather than a text-* class in `className` (which would conflict
   * with the size class this component emits).
   */
  size?: EyebrowSize;
  /** letterSpacing value — defaults to "0.22em". Pass "0.18em" for denser contexts. */
  tracking?: string;
  /** Renders the eyebrow inside the standard pill chrome (SectionHeading's header pill). */
  pill?: boolean;
  className?: string;
  children: ReactNode;
};

const sizeClass: Record<EyebrowSize, string> = {
  "2xs": "text-2xs",
  xs: "text-xs",
  sm: "text-sm",
  base: "text-base",
};

export function Eyebrow({
  tone = "primary",
  size = "xs",
  tracking = "0.22em",
  pill = false,
  className,
  children,
}: EyebrowProps) {
  const base = clsx(sizeClass[size], "font-semibold uppercase");
  const color =
    tone === "inherit" ? "" : tone === "muted" ? "text-text-muted" : "text-primary";
  const style: CSSProperties = { letterSpacing: tracking };

  return (
    <div
      className={clsx(
        base,
        color,
        pill && "inline-flex items-center rounded-full border border-border bg-surface px-3 py-1",
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
